import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { Check, X, Trash2, Mail, Phone, Package, Gift, CreditCard, Truck, Search } from 'lucide-react';
import { Spinner } from '../components/Spinner';
import { InlineSpinner } from '../components/InlineSpinner';
import { CopyableCode } from '../components/CopyableCode';
import { Pagination } from '../components/Pagination';
import { SortOrderToggle, SortOrder } from '../components/SortOrderToggle';
import { usePagination } from '../hooks/usePagination';
import { useConfirm } from '../contexts/ConfirmDialogContext';
import styles from './ColorPaletteOrdersPage.module.css';

interface PaletteImage {
  url: string;
  isDefault: boolean;
}

interface ColorPalette {
  _id: string;
  name: string;
  price: number;
  imageUrl: string;
  images?: PaletteImage[];
}

type PaymentMethod = 'cod' | 'instapay' | 'wallet';

interface ColorPaletteOrder {
  _id: string;
  referenceNumber: string;
  colorPalette: ColorPalette;
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  deliveryAddress: string;
  governorate: string;
  area: string;
  isGift: boolean;
  giftFee: number;
  giftMessage: string;
  status: 'pending' | 'confirmed' | 'cancelled';
  paymentMethod: PaymentMethod;
  isPaid: boolean;
  quantity: number;
  originalUnitPrice: number;
  unitPrice: number;
  discountPercent: number;
  totalPrice: number;
  shippingCost: number;
  createdAt: string;
}

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cod: 'Cash on Delivery',
  instapay: 'Instapay',
  wallet: 'Mobile Wallet',
};

function getPaletteImageSrc(palette: ColorPalette) {
  const url =
    palette.images && palette.images.length > 0
      ? (palette.images.find((img) => img.isDefault) || palette.images[0]).url
      : palette.imageUrl;
  if (!url) return '';
  return url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
}

export function ColorPaletteOrdersPage() {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [orders, setOrders] = useState<ColorPaletteOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const [selectedOrder, setSelectedOrder] = useState<ColorPaletteOrder | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const isBusy = (id: string, action: string) => busyAction === `${id}:${action}`;
  const isRowBusy = (id: string) => !!busyAction && busyAction.startsWith(`${id}:`);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/color-palette-orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setOrders(data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [token]);

  const updateStatus = async (id: string, status: 'confirmed' | 'cancelled') => {
    setBusyAction(`${id}:${status === 'confirmed' ? 'confirm' : 'cancel'}`);
    try {
      const response = await fetch(`${API_BASE_URL}/api/color-palette-orders/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) throw new Error('Failed to update status');
      await fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      alert('Failed to update order status');
    } finally {
      setBusyAction(null);
    }
  };

  const togglePaid = async (id: string, currentIsPaid: boolean) => {
    setBusyAction(`${id}:paid`);
    try {
      const response = await fetch(`${API_BASE_URL}/api/color-palette-orders/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isPaid: !currentIsPaid }),
      });

      if (!response.ok) throw new Error('Failed to update payment status');
      const data = await response.json();
      setOrders((prev) => prev.map((o) => (o._id === id ? data.order : o)));
      setSelectedOrder((prev) => (prev && prev._id === id ? data.order : prev));
    } catch (error) {
      alert('Failed to update payment status');
    } finally {
      setBusyAction(null);
    }
  };

  const deleteOrder = async (id: string) => {
    const confirmed = await confirm({
      message: 'Are you sure you want to delete this order? This cannot be undone.',
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;

    setBusyAction(`${id}:delete`);
    try {
      const response = await fetch(`${API_BASE_URL}/api/color-palette-orders/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to delete');
      await fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      alert('Failed to delete order');
    } finally {
      setBusyAction(null);
    }
  };

  const filteredOrders = orders
    .filter((o) => {
      if (filter !== 'all' && o.status !== filter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        return (
          o.referenceNumber?.toLowerCase().includes(q) ||
          o.guestName.toLowerCase().includes(q) ||
          o.guestPhone.includes(q)
        );
      }
      return true;
    })
    .sort((a, b) => {
      const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return sortOrder === 'newest' ? -diff : diff;
    });

  const { page, setPage, pageCount, pageItems, totalItems } = usePagination(filteredOrders, 20);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const badgeStyles: Record<string, React.CSSProperties> = {
      pending: { background: '#fef3c7', color: '#92400e' },
      confirmed: { background: '#d1fae5', color: '#065f46' },
      cancelled: { background: '#fee2e2', color: '#991b1b' },
    };
    return badgeStyles[status] || {};
  };

  const getPaidBadgeStyle = (isPaid: boolean): React.CSSProperties =>
    isPaid
      ? { background: '#d1fae5', color: '#065f46' }
      : { background: '#fee2e2', color: '#991b1b' };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>Color Palette Orders</h1>
          <p>Manage color palette orders from customers</p>
        </div>
        <div className={styles.filters}>
          <div className={styles.searchBox}>
            <Search size={16} />
            <input
              type="text"
              placeholder="Search by reference #, name, or phone"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {['all', 'pending', 'confirmed', 'cancelled'].map((f) => (
            <button
              key={f}
              className={`${styles.filterBtn} ${filter === f ? styles.filterActive : ''}`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
          <SortOrderToggle value={sortOrder} onChange={setSortOrder} />
        </div>
      </header>

      {loading ? (
        <Spinner label="Loading orders..." size="lg" />
      ) : filteredOrders.length === 0 ? (
        <div className={styles.empty}>
          <p>No {filter !== 'all' ? filter : ''} color palette orders found.</p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Order</th>
                <th>Customer</th>
                <th>Contact</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((order) => (
                <tr key={order._id} onClick={() => setSelectedOrder(order)}>
                  <td>
                    <div className={styles.orderInfo}>
                      {getPaletteImageSrc(order.colorPalette) && (
                        <img
                          src={getPaletteImageSrc(order.colorPalette)}
                          alt={order.colorPalette?.name}
                          className={styles.kitImage}
                        />
                      )}
                      <div>
                        <strong>{order.colorPalette?.name || 'Unknown Palette'}</strong>
                        <span className={styles.kitDetails}>
                          Qty {order.quantity}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.customerInfo}>
                      <strong>{order.guestName}</strong>
                      {order.isGift && (
                        <span className={styles.giftBadge}>
                          <Gift size={12} /> Gift
                        </span>
                      )}
                      {order.referenceNumber && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <CopyableCode value={order.referenceNumber} />
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className={styles.contactInfo}>
                      <span><Mail size={14} /> {order.guestEmail}</span>
                      <span><Phone size={14} /> {order.guestPhone}</span>
                    </div>
                  </td>
                  <td>
                    <strong>EGP {order.totalPrice + order.shippingCost}</strong>
                    <div className={styles.kitDetails}>
                      {order.discountPercent > 0 && `${order.discountPercent}% off • `}
                      Shipping EGP {order.shippingCost}
                    </div>
                  </td>
                  <td>
                    <div className={styles.customerInfo}>
                      <span className={styles.kitDetails}>
                        {PAYMENT_METHOD_LABELS[order.paymentMethod] || order.paymentMethod}
                      </span>
                      <span className={styles.statusBadge} style={getPaidBadgeStyle(order.isPaid)}>
                        {order.isPaid ? 'Paid' : 'Unpaid'}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className={styles.statusBadge} style={getStatusBadge(order.status)}>
                      {order.status}
                    </span>
                  </td>
                  <td className={styles.dateCell}>{formatDate(order.createdAt)}</td>
                  <td>
                    <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
                      {order.status === 'pending' && (
                        <>
                          <button
                            className={styles.confirmBtn}
                            onClick={() => updateStatus(order._id, 'confirmed')}
                            disabled={isRowBusy(order._id)}
                            title="Confirm"
                          >
                            {isBusy(order._id, 'confirm') ? <InlineSpinner size={16} /> : <Check size={16} />}
                          </button>
                          <button
                            className={styles.cancelBtn}
                            onClick={() => updateStatus(order._id, 'cancelled')}
                            disabled={isRowBusy(order._id)}
                            title="Cancel"
                          >
                            {isBusy(order._id, 'cancel') ? <InlineSpinner size={16} /> : <X size={16} />}
                          </button>
                        </>
                      )}
                      <button
                        className={styles.deleteBtn}
                        onClick={() => deleteOrder(order._id)}
                        disabled={isRowBusy(order._id)}
                        title="Delete"
                      >
                        {isBusy(order._id, 'delete') ? <InlineSpinner size={16} /> : <Trash2 size={16} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page}
            pageCount={pageCount}
            totalItems={totalItems}
            onPageChange={setPage}
          />
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className={styles.modalOverlay} onClick={() => setSelectedOrder(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeBtn} onClick={() => setSelectedOrder(null)}>
              <X size={20} />
            </button>

            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <div>
                  <h2>Order Details</h2>
                  {selectedOrder.referenceNumber && (
                    <CopyableCode value={selectedOrder.referenceNumber} />
                  )}
                </div>
                <span className={styles.statusBadge} style={getStatusBadge(selectedOrder.status)}>
                  {selectedOrder.status}
                </span>
              </div>

              <div className={styles.detailSection}>
                <h3><Package size={18} /> Color Palette</h3>
                <div className={styles.kitCard}>
                  {getPaletteImageSrc(selectedOrder.colorPalette) && (
                    <img
                      src={getPaletteImageSrc(selectedOrder.colorPalette)}
                      alt={selectedOrder.colorPalette?.name}
                      className={styles.modalKitImage}
                    />
                  )}
                  <div className={styles.kitInfo}>
                    <h4>{selectedOrder.colorPalette?.name || 'Unknown Palette'}</h4>
                    <p>Quantity: {selectedOrder.quantity}</p>
                    <p>
                      Unit Price: EGP {selectedOrder.unitPrice}
                      {selectedOrder.discountPercent > 0 && (
                        <> ({selectedOrder.discountPercent}% off EGP {selectedOrder.originalUnitPrice})</>
                      )}
                    </p>
                    <p className={styles.kitPrice}>
                      Total: EGP {selectedOrder.totalPrice + selectedOrder.shippingCost}{' '}
                      <span style={{ fontWeight: 400, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        (incl. EGP {selectedOrder.shippingCost} shipping)
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h3><CreditCard size={18} /> Payment</h3>
                <div className={styles.detailGrid}>
                  <div>
                    <label>Method</label>
                    <p>{PAYMENT_METHOD_LABELS[selectedOrder.paymentMethod] || selectedOrder.paymentMethod}</p>
                  </div>
                  <div>
                    <label>Status</label>
                    <p>
                      <span className={styles.statusBadge} style={getPaidBadgeStyle(selectedOrder.isPaid)}>
                        {selectedOrder.isPaid ? 'Paid' : 'Unpaid'}
                      </span>
                    </p>
                  </div>
                </div>
                <button
                  className={selectedOrder.isPaid ? styles.cancelBtnLarge : styles.confirmBtnLarge}
                  style={{ marginTop: '1rem' }}
                  onClick={() => togglePaid(selectedOrder._id, selectedOrder.isPaid)}
                  disabled={isRowBusy(selectedOrder._id)}
                >
                  {isBusy(selectedOrder._id, 'paid') ? <InlineSpinner size={18} /> : <CreditCard size={18} />}
                  {selectedOrder.isPaid ? 'Mark as Unpaid' : 'Mark as Paid'}
                </button>
              </div>

              <div className={styles.detailSection}>
                <h3><Truck size={18} /> Delivery</h3>
                <div className={styles.detailGrid}>
                  <div>
                    <label>Address</label>
                    <p>{selectedOrder.deliveryAddress}</p>
                  </div>
                  <div>
                    <label>Governorate</label>
                    <p>
                      {selectedOrder.governorate}
                      {selectedOrder.area ? ` (${selectedOrder.area})` : ''}
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h3>Customer Information</h3>
                <div className={styles.detailGrid}>
                  <div>
                    <label>Name</label>
                    <p>{selectedOrder.guestName}</p>
                  </div>
                  <div>
                    <label>Email</label>
                    <p><Mail size={14} /> {selectedOrder.guestEmail}</p>
                  </div>
                  <div>
                    <label>Phone</label>
                    <p><Phone size={14} /> {selectedOrder.guestPhone}</p>
                  </div>
                  <div>
                    <label>Order Date</label>
                    <p>{formatDate(selectedOrder.createdAt)}</p>
                  </div>
                </div>
              </div>

              {selectedOrder.isGift && (
                <div className={styles.detailSection}>
                  <h3><Gift size={18} /> Gift Order</h3>
                  {selectedOrder.giftFee > 0 && (
                    <p>Gift Fee: EGP {selectedOrder.giftFee}</p>
                  )}
                  {selectedOrder.giftMessage && (
                    <div className={styles.giftMessage}>
                      <label>Gift Message</label>
                      <p>"{selectedOrder.giftMessage}"</p>
                    </div>
                  )}
                </div>
              )}

              <div className={styles.modalActions}>
                {selectedOrder.status === 'pending' && (
                  <>
                    <button
                      className={styles.confirmBtnLarge}
                      onClick={() => updateStatus(selectedOrder._id, 'confirmed')}
                      disabled={isRowBusy(selectedOrder._id)}
                    >
                      {isBusy(selectedOrder._id, 'confirm') ? <InlineSpinner size={18} /> : <Check size={18} />}
                      Confirm Order
                    </button>
                    <button
                      className={styles.cancelBtnLarge}
                      onClick={() => updateStatus(selectedOrder._id, 'cancelled')}
                      disabled={isRowBusy(selectedOrder._id)}
                    >
                      {isBusy(selectedOrder._id, 'cancel') ? <InlineSpinner size={18} /> : <X size={18} />}
                      Cancel Order
                    </button>
                  </>
                )}
                <button
                  className={styles.deleteBtnLarge}
                  onClick={() => deleteOrder(selectedOrder._id)}
                  disabled={isRowBusy(selectedOrder._id)}
                >
                  {isBusy(selectedOrder._id, 'delete') ? <InlineSpinner size={18} /> : <Trash2 size={18} />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
